<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { roleBadge, roleLabel } from '@/composables/useRoles';

interface Role { id: number; role: string; }

const roles = ref<Role[]>([]);
const loading = ref(true);
const newRoleName = ref('');
const creating = ref(false);
const editingId = ref<number | null>(null);
const editingName = ref('');

const PROTECTED = ['super_admin', 'player'];

const load = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/users/roles');
    roles.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const createRole = async () => {
  if (!newRoleName.value.trim()) return;
  creating.value = true;
  try {
    const { data } = await dartsHubApi.post('/users/roles', { role: newRoleName.value.trim() });
    roles.value.push(data);
    newRoleName.value = '';
    toast.success('Rôle créé.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    creating.value = false;
  }
};

const startEdit = (role: Role) => {
  editingId.value = role.id;
  editingName.value = role.role;
};

const saveEdit = async (role: Role) => {
  try {
    await dartsHubApi.patch(`/users/roles/${role.id}`, { role: editingName.value });
    role.role = editingName.value;
    editingId.value = null;
    toast.success('Rôle mis à jour.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

const deleteRole = async (role: Role) => {
  if (PROTECTED.includes(role.role)) {
    toast.error('Ce rôle est protégé.');
    return;
  }
  if (!confirm(`Supprimer le rôle "${role.role}" ?`)) return;
  try {
    await dartsHubApi.delete(`/users/roles/${role.id}`);
    roles.value = roles.value.filter(r => r.id !== role.id);
    toast.success('Rôle supprimé.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Ce rôle est assigné à des utilisateurs.');
  }
};

onMounted(load);
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-shield-halved" /> Rôles</h1>
    </div>

    <div class="roles-layout">
      <!-- Liste des rôles -->
      <div class="card">
        <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>
        <div v-else class="roles-list">
          <div v-for="role in roles" class="role-row" :key="role.id">
            <div v-if="editingId === role.id" class="role-edit">
              <input v-model="editingName" type="text" class="role-input" @keyup.enter="saveEdit(role)" />
              <button class="btn btn-primary btn-sm" @click="saveEdit(role)">
                <i class="fa-solid fa-check" />
              </button>
              <button class="btn btn-ghost btn-sm" @click="editingId = null">
                <i class="fa-solid fa-xmark" />
              </button>
            </div>
            <div v-else class="role-display">
              <span class="badge" :class="roleBadge[role.role] || 'badge-accent'">{{ roleLabel[role.role] || role.role
                }}</span>
              <span class="role-technical">{{ role.role }}</span>
              <div class="role-actions">
                <button v-if="!PROTECTED.includes(role.role)" class="btn btn-ghost btn-sm" @click="startEdit(role)">
                  <i class="fa-solid fa-pen" />
                </button>
                <button v-if="!PROTECTED.includes(role.role)" class="btn btn-danger btn-sm" @click="deleteRole(role)">
                  <i class="fa-solid fa-trash" />
                </button>
                <span v-else class="protected-label">Protégé</span>
              </div>
            </div>
          </div>
          <div v-if="roles.length === 0" class="empty">Aucun rôle</div>
        </div>
      </div>

      <!-- Création -->
      <div class="card">
        <h2 style="font-size:1rem;font-weight:600;margin-bottom:1rem">Créer un rôle</h2>
        <div class="form-group">
          <label>Nom du rôle</label>
          <input v-model="newRoleName" type="text" placeholder="ex: comite_admin" @keyup.enter="createRole" />
          <small style="color:var(--text-muted)">Utiliser snake_case, sans espaces.</small>
        </div>
        <button class="btn btn-primary" :disabled="creating || !newRoleName.trim()" @click="createRole">
          <i v-if="creating" class="fa-solid fa-spinner fa-spin" />
          <i v-else class="fa-solid fa-plus" />
          Créer
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.roles-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1.5rem;
  align-items: start;
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-row {
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
}

.role-row:last-child {
  border-bottom: none;
}

.role-technical {
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--text-muted);
  flex: 1;
}

.role-display,
.role-edit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.role-actions {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.role-input {
  flex: 1;
  background: var(--bg-input);
  border: 1px solid var(--primary);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.3rem 0.5rem;
  font-size: 0.875rem;
}

.protected-label {
  color: var(--text-muted);
  font-size: 0.75rem;
}

.empty {
  color: var(--text-muted);
  text-align: center;
  padding: 1rem;
}

@media (max-width: 768px) {
  .roles-layout {
    grid-template-columns: 1fr;
  }
}
</style>
